import { Recipe } from '../types/recipe'
import { clientLogger } from './clientLogger'


export function parseRecipeResponse(content: string): Recipe[] {
  clientLogger.debug({ contentPreview: content.substring(0, 200) }, 'Parsing recipe content')
  const recipes: Recipe[] = []
  
  // Stop parsing at the end marker if it exists
  const endMarkerIndex = content.indexOf('---END_RECIPES---')
  const contentToParse = endMarkerIndex !== -1 ? content.substring(0, endMarkerIndex) : content
  
  clientLogger.debug({ hasEndMarker: endMarkerIndex !== -1, endMarkerIndex, originalLength: content.length, parsedLength: contentToParse.length }, 'Recipe content parsed')
  
  // Split by meal type sections
  const sections = contentToParse.split(/\*\*([A-Z]+)\s*\(.*?\):\*\*/i)
  clientLogger.debug({ sectionCount: sections.length }, 'Found recipe sections')
  
  let currentMealType: 'breakfast' | 'lunch' | 'dinner' | 'snacks' | 'dessert' | null = null

  for (let i = 0; i < sections.length; i++) {
    const section = sections[i]?.trim()
    if (!section) continue

    // Check if this section is a meal type header
    if (i % 2 === 1) { // Odd indices are the captured meal type names
      const mealTypeName = section.toLowerCase()
      if (mealTypeName.includes('breakfast')) {
        currentMealType = 'breakfast'
      } else if (mealTypeName.includes('lunch')) {
        currentMealType = 'lunch'
      } else if (mealTypeName.includes('dinner')) {
        currentMealType = 'dinner'
      } else if (mealTypeName.includes('snack')) {
        currentMealType = 'snacks'
      } else if (mealTypeName.includes('dessert')) {
        currentMealType = 'dessert'
      }
    } else if (currentMealType && i % 2 === 0) {
      // This is a meal list section
      const lines = section.split('\n')
      
      for (const line of lines) {
        const trimmed = line.trim()
        // Look for lines that start with "- " (meal items) or numbered lists "1. ", "2. ", etc.
        if ((trimmed.startsWith('- ') && trimmed.length > 2) || 
            (/^\d+\.\s/.test(trimmed) && trimmed.length > 3)) {
          
          let recipeName = ''
          if (trimmed.startsWith('- ')) {
            recipeName = trimmed.substring(2).trim()
          } else if (/^\d+\.\s/.test(trimmed)) {
            // Handle numbered lists like "1. Recipe Name"
            recipeName = trimmed.replace(/^\d+\.\s/, '').trim()
          }
          
          if (recipeName) {
            recipes.push({
              id: `${currentMealType}-${recipes.length}`,
              name: recipeName,
              description: '',
              ingredients: [],
              instructions: [],
              prepTime: 0,
              cookTime: 0,
              servings: 0,
              nutrition: null,
              tags: [],
              mealType: currentMealType
            })
          }
        }
      }
    }
  }
  
  clientLogger.debug({ recipeCount: recipes.length }, 'Extracted recipes')
  return recipes
}

export function isRecipeResponse(content: string): boolean {
  // Check if the content contains meal planning format
  const hasMealSections = /\*\*([A-Z]+)\s*\(.*?\):\*\*/i.test(content)
  const hasDashItems = content.includes('- ')
  const hasMealWords = /(breakfast|lunch|dinner|meal)/i.test(content)
  
  return hasMealSections && hasDashItems && hasMealWords
}

// For debugging
export function debugParseRecipeResponse(content: string): any {
  clientLogger.debug({ contentLength: content.length }, 'Debug parsing recipe content')

  const sections = content.split(/\*\*([A-Z]+)\s*\(.*?\):\*\*/i)
  clientLogger.debug({ sectionCount: sections.length }, 'Debug split sections')

  const recipes = parseRecipeResponse(content)
  clientLogger.debug({ recipeCount: recipes.length }, 'Debug extracted recipes')
  
  return { sections, recipes }
}